/**
 * State Manager for centralized application state management
 * Handles test results, contour data, and application state with observer pattern
 */

import { Logger } from '../core/logger';
import { presetFrequencies } from '../features/preset-manager/presets';
import type { TestResult, TestResults, EqualLoudnessContour } from '../types';

const logger = Logger.createComponentLogger('StateManager');

type ChangeEvent = 'results' | 'contours' | 'frequency' | 'all';

class StateManager {
    testResults: TestResults;
    equalLoudnessContours: { [key: string]: EqualLoudnessContour[] };
    currentFrequency: number;
    presetFrequencies: number[];
    currentPresetIndex: number;
    observers: Array<(data?: unknown) => void>;
    changeListeners: {
        results: Array<(data?: unknown) => void>;
        contours: Array<(data?: unknown) => void>;
        frequency: Array<(data?: unknown) => void>;
        all: Array<(data?: unknown) => void>;
    };

    constructor() {
        this.testResults = { both: [], left: [], right: [] };
        this.equalLoudnessContours = {};
        this.currentFrequency = 1000;
        this.presetFrequencies = presetFrequencies;
        this.currentPresetIndex = this.presetFrequencies.indexOf(1000);
        
        // Observer pattern for state changes
        this.observers = [];
        this.changeListeners = {
            'results': [],
            'contours': [],
            'frequency': [],
            'all': []
        };
        
        logger.info('StateManager initialized');
    }

    /**
     * Subscribe to state changes
     * @param {string} eventType - Type of change to listen for ('results', 'contours', 'frequency', 'all')
     * @param {Function} callback - Callback function to call when state changes
     */
    subscribe(eventType: ChangeEvent, callback: (data?: unknown) => void): void {
        const listeners = this.changeListeners[eventType];
        if (listeners) {
            listeners.push(callback);
        } else {
            logger.warn('Unknown event type:', eventType);
        }
    }

    /**
     * Unsubscribe from state changes
     * @param {string} eventType - Type of change to stop listening for
     * @param {Function} callback - Callback function to remove
     */
    unsubscribe(eventType: ChangeEvent, callback: (data?: unknown) => void): void {
        const listeners = this.changeListeners[eventType];
        if (listeners) {
            const index = listeners.indexOf(callback);
            if (index > -1) {
                listeners.splice(index, 1);
            }
        }
    }

    /**
     * Notify observers of state changes
     * @param {string} changeType - Type of change that occurred
     * @param {Object} changeData - Data related to the change
     */
    notify(changeType: ChangeEvent, changeData: unknown = {}): void {
        logger.debug('StateManager: notifying change', changeType, changeData);
        
        // Notify specific event listeners
        const specificListeners = this.changeListeners[changeType];
        if (specificListeners) {
            specificListeners.forEach((callback: (data?: unknown) => void) => {
                try {
                    callback(changeData);
                } catch (error) {
                    logger.error('Error in state change callback:', error);
                }
            });
        }

        // Notify 'all' listeners
        this.changeListeners.all.forEach((callback: (data?: unknown) => void) => {
            try {
                callback({ type: changeType, data: changeData });
                } catch (error) {
                    logger.error('Error in state change callback:', error);
                }
        });

        // Notify observers (legacy observer API)
        this.observers.forEach((callback: (data?: unknown) => void) => {
            try {
                callback({ type: changeType, data: changeData });
            } catch (error) {
                logger.error('Error in observer callback:', error);
            }
        });
    }

    /**
     * Update a test result
     * @param {number} frequency - Frequency in Hz
     * @param {number} db - Decibel value
     * @param {string} ear - Which ear ('both', 'left', 'right')
     */
    updateResult(frequency: number, db: number, ear: 'left' | 'right' | 'both' = 'both'): void {
        // Input validation
        if (typeof frequency !== 'number' || !isFinite(frequency) || frequency <= 0) {
            logger.error('StateManager: Invalid frequency value:', frequency);
            return;
        }
        
        if (typeof db !== 'number' || !isFinite(db)) {
            logger.error('StateManager: Invalid dB value:', db);
            return;
        }
        
        if (!['left', 'right', 'both'].includes(ear)) {
            logger.error('StateManager: Invalid ear value:', ear);
            return;
        }
        
        const results = this.testResults[ear];
        
        // Find existing result for this frequency
        const existingIndex = results.findIndex((result: TestResult) => Math.abs(result.frequency - frequency) < 1);
        
        if (existingIndex !== -1) {
            // Update existing result
            results[existingIndex].db = db;
        } else {
            // Add new result
            results.push({ frequency: frequency, db: db });
        }
        
        logger.info(`StateManager: updated result for ${ear} ear: ${frequency}Hz -> ${db}dB`);
        
        // Notify observers
        this.notify('results', { 
            ear: ear, 
            frequency: frequency, 
            db: db, 
            results: this.testResults[ear] 
        });
    }

    /**
     * Add a test result (alias for updateResult for API compatibility)
     * @param {number} frequency - Frequency in Hz
     * @param {number} db - Decibel value
     * @param {'both'|'left'|'right'} ear - Which ear ('both', 'left', 'right')
     */
    addResult(frequency: number, db: number, ear: 'left' | 'right' | 'both' = 'both'): void {
        return this.updateResult(frequency, db, ear);
    }

    /**
     * Get test results for a specific ear
     * @param {'both'|'left'|'right'} ear - Which ear ('both', 'left', 'right')
     * @returns {Array} Array of {frequency, db} objects
     */
    getResults(ear: 'left' | 'right' | 'both' = 'both'): TestResult[] {
        return [...this.testResults[ear]]; // Return copy to prevent external mutation
    }

    /**
     * Get all test results
     * @returns {Object} Object with both, left, right arrays
     */
    getAllResults(): TestResults {
        return {
            both: [...this.testResults.both],
            left: [...this.testResults.left],
            right: [...this.testResults.right]
        };
    }

    /**
     * Reset test results for specific ear or all ears
     * @param {'both'|'left'|'right'|'all'} ear - Which ear to reset ('both', 'left', 'right', 'all')
     */
    resetResults(ear: 'both' | 'left' | 'right' | 'all' = 'all'): void {
        if (ear === 'all') {
            this.testResults.both = [];
            this.testResults.left = [];
            this.testResults.right = [];
        } else {
            this.testResults[ear] = [];
        }
        
        logger.info(`StateManager: reset results for ${ear}`);
        this.notify('results', { ear: ear, action: 'reset' });
    }

    /**
     * Load equal loudness contours
     * @param {Object} contours - Contour data object
     */
    loadContours(contours: { [key: string]: EqualLoudnessContour[] }): void {
        this.equalLoudnessContours = contours;
        logger.info('StateManager: loaded contours:', Object.keys(contours));
        this.notify('contours', { contours: contours });
    }

    /**
     * Backwards-compatible alias for older callers that expect setEqualLoudnessContours
     * Keeps runtime compatibility with code that was updated to use the typed method name.
     * @param {Object} contours - Contour data object
     */
    setEqualLoudnessContours(contours: { [key: string]: EqualLoudnessContour[] }): void {
        // Delegate to the existing loadContours implementation
        this.loadContours(contours);
    }

    /**
     * Get equal loudness contours
     * @returns {Object} Contour data object
     */
    getContours(): { [key: string]: EqualLoudnessContour[] } {
        return this.equalLoudnessContours;
    }

    /**
     * Set current frequency
     * @param {number} frequency - Frequency in Hz
     */
    setCurrentFrequency(frequency: number): void {
        this.currentFrequency = frequency;
        
        // Update preset index if frequency matches a preset
        const presetIndex = this.presetFrequencies.indexOf(frequency);
        if (presetIndex !== -1) {
            this.currentPresetIndex = presetIndex;
        }
        
        logger.debug(`StateManager: set current frequency to ${frequency}Hz`);
        this.notify('frequency', { frequency: frequency });
    }

    /**
     * Get current frequency
     * @returns {number} Current frequency in Hz
     */
    getCurrentFrequency() {
        return this.currentFrequency;
    }

    /**
     * Get preset frequencies
     * @returns {Array} Array of preset frequencies
     */
    getPresetFrequencies() {
        return [...this.presetFrequencies];
    }

    /**
     * Get current preset index
     * @returns {number} Current preset index
     */
    getCurrentPresetIndex() {
        return this.currentPresetIndex;
    }

    /**
     * Move to next preset frequency
     * @returns {number} New frequency
     */
    nextPresetFrequency() {
        if (this.currentPresetIndex < this.presetFrequencies.length - 1) {
            this.currentPresetIndex++;
            this.currentFrequency = this.presetFrequencies[this.currentPresetIndex];
            this.notify('frequency', { frequency: this.currentFrequency });
        }
        return this.currentFrequency;
    }

    /**
     * Move to previous preset frequency
     * @returns {number} New frequency
     */
    previousPresetFrequency() {
        if (this.currentPresetIndex > 0) {
            this.currentPresetIndex--;
            this.currentFrequency = this.presetFrequencies[this.currentPresetIndex];
            this.notify('frequency', { frequency: this.currentFrequency });
        }
        return this.currentFrequency;
    }

    /**
     * Get complete current state snapshot
     * @returns {Object} Complete state object
     */
    getCurrentState() {
        return {
            testResults: this.getAllResults(),
            equalLoudnessContours: this.getContours(),
            currentFrequency: this.getCurrentFrequency(),
            presetFrequencies: this.getPresetFrequencies(),
            currentPresetIndex: this.getCurrentPresetIndex()
        };
    }

    /**
     * Restore state from snapshot
     * @param {Object} state - State object to restore
     */
    restoreState(state: Partial<{ testResults: TestResults; equalLoudnessContours: { [key: string]: EqualLoudnessContour[] }; currentFrequency: number; currentPresetIndex: number }>): void {
        if (state.testResults) {
            this.testResults = {
                both: [...(state.testResults.both || [])],
                left: [...(state.testResults.left || [])],
                right: [...(state.testResults.right || [])]
            };
        }
        
        if (state.equalLoudnessContours) {
            this.equalLoudnessContours = state.equalLoudnessContours;
        }
        
        if (state.currentFrequency) {
            this.currentFrequency = state.currentFrequency;
        }
        
        if (state.currentPresetIndex !== undefined) {
            this.currentPresetIndex = state.currentPresetIndex;
        }
        
        logger.info('StateManager: restored state');
        this.notify('all', { action: 'restore', state: state });
    }

    /**
     * Export state for persistence
     * @returns {Object} Serializable state object
     */
    exportState() {
        return {
            testResults: this.getAllResults(),
            currentFrequency: this.getCurrentFrequency(),
            currentPresetIndex: this.getCurrentPresetIndex(),
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Get statistics about current test results
     * @param {'both'|'left'|'right'} ear - Which ear to analyze
     * @returns {Object} Statistics object
     */
    getResultStatistics(ear: 'left' | 'right' | 'both' = 'both'): { count: number; minDb: number | null; maxDb: number | null; avgDb: number | null; frequencyRange: { min: number; max: number } } {
        // Input validation
        if (!['left', 'right', 'both'].includes(ear)) {
            logger.warn('StateManager: Invalid ear value for getResultStatistics:', ear);
            return { count: 0, minDb: null, maxDb: null, avgDb: null, frequencyRange: { min: 0, max: 0 } };
        }
        
        const results = this.testResults[ear];
        
        if (results.length === 0) {
            return { count: 0, minDb: null, maxDb: null, avgDb: null, frequencyRange: { min: 0, max: 0 } };
        }
        
        // Filter out invalid results for robustness
        const validResults = results.filter(r => 
            r && 
            typeof r.frequency === 'number' && isFinite(r.frequency) && 
            typeof r.db === 'number' && isFinite(r.db)
        );
        
        if (validResults.length === 0) {
            return { count: 0, minDb: null, maxDb: null, avgDb: null, frequencyRange: { min: 0, max: 0 } };
        }
        
        const dbValues = validResults.map((r: TestResult) => r.db);
        const minDb = Math.min(...dbValues);
        const maxDb = Math.max(...dbValues);
        const avgDb = dbValues.reduce((sum: number, db: number) => sum + db, 0) / dbValues.length;
        
        return {
            count: validResults.length,
            minDb: minDb,
            maxDb: maxDb,
            avgDb: avgDb,
            frequencyRange: {
                min: Math.min(...validResults.map((r: TestResult) => r.frequency)),
                max: Math.max(...validResults.map((r: TestResult) => r.frequency))
            }
        };
    }

    /**
     * Observer helpers (compatibility shims)
     * These methods are small, backwards-compatible aliases to support older call sites
     * TODO: TECHNICAL DEBT - prefer using the typed change listener API and remove these aliases later
     */
    addObserver(callback: (data?: unknown) => void): void {
        this.observers.push(callback);
    }

    removeObserver(callback: (data?: unknown) => void): void {
        const idx = this.observers.indexOf(callback);
        if (idx > -1) this.observers.splice(idx, 1);
    }

    addChangeListener(type: ChangeEvent, callback: (data?: unknown) => void): void {
        this.subscribe(type, callback);
    }

    removeChangeListener(type: ChangeEvent, callback: (data?: unknown) => void): void {
        this.unsubscribe(type, callback);
    }

    clearResults(ear: 'both' | 'left' | 'right' | 'all' = 'all'): void {
        // Delegate to existing resetResults implementation
        this.resetResults(ear);
    }

    getEqualLoudnessContours(): { [key: string]: EqualLoudnessContour[] } {
        return this.getContours();
    }

    nextPreset(): number {
        return this.nextPresetFrequency();
    }

    previousPreset(): number {
        return this.previousPresetFrequency();
    }

    resetState(): void {
        // Reset to a sane default state
        this.testResults = { both: [], left: [], right: [] };
        this.equalLoudnessContours = {};
        this.currentFrequency = this.presetFrequencies[0] || 1000;
        this.currentPresetIndex = 0;
        this.notify('all', { action: 'reset' });
    }
}

// ES module export
export { StateManager };
export default StateManager;
